import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm } from "react-hook-form";
import { createDetailsFormSchema } from "./formSchema";
import { useTranslation } from "react-i18next";
import { ContractFormGroup, DeadlineFormGroup, DescriptionFormGroup, EstimateFormGroup } from "./FormGroups";
import { Form } from "@/components/ui/form";
import { Tender, TenderType } from "@/graphql/__generated__/graphql";
import { z } from "zod";
import { convertBpToDecimalOrUndefined, convertDecimalToBp } from "@/utils/helpers";
import ContractDurationForm, { createContractDurationSchema } from "./ContractDurationForm";
import { Dispatch, SetStateAction, useImperativeHandle, Ref, useState, useEffect } from "react";
import { useUpdateTenderContractDurationMutationHook, useUpdateTenderDetailsMutationHook } from "@/hooks";
import ServiceRecalculationDialog from "./Components/ServiceRecalculationDialog";
import { CustomToastComponent } from "@/components/shared";
import { toast } from "react-toastify";

interface DetailsFormComponentProps {
  tenderData: Partial<Tender>;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  setIsSaving: Dispatch<SetStateAction<boolean>>;
  ref: Ref<{ handleSave: () => void }>;
}

/**
 * DetailsFormComponent renders the details form for a tender
 * @param tenderData - The tender data
 * @param setIsEditing - Function to set editing state
 * @param ref - The ref object to expose handleSave
 * @returns The rendered details form component
 */
const DetailsFormComponent = ({ tenderData, setIsEditing, setIsSaving, ref }: DetailsFormComponentProps) => {
  const { t } = useTranslation();

  const [isServiceRecalculatingDialogOpen, setIsServiceRecalculatingDialogOpen] = useState(false);
  const [contractDurationValues, setContractDurationValues] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    startDate: undefined,
    endDate: undefined
  });

  const { updateTenderDetailsMutation, loading: isUpdatingTenderDetails } = useUpdateTenderDetailsMutationHook();
  const { updateTenderContractDurationMutation, loading: isUpdatingTenderContractDuration } = useUpdateTenderContractDurationMutationHook();

  const tenderContractStartDate = tenderData.contract?.startDate ? new Date(tenderData.contract.startDate) : undefined;
  const tenderContractEndDate = tenderData.contract?.endDate ? new Date(tenderData.contract.endDate) : undefined;

  // FIXME: This isnt the safest method to know if there are services on the tender.
  // Because there can be services than does not provide revenue such as additional services.
  const hasServices = tenderData.revenue ? tenderData.revenue > 0 : false;

  const detailsFormSchema = createDetailsFormSchema(t);
  const detailsForm = useForm<z.infer<typeof detailsFormSchema>>({
    resolver: zodResolver(detailsFormSchema) as Resolver<z.infer<typeof detailsFormSchema>>,
    defaultValues: {
      description: {
        name: tenderData.description?.name ?? undefined,
        province: tenderData.description?.province ?? undefined,
        type: tenderData.description?.type as TenderType.Private | TenderType.Public | undefined,
        platform: tenderData.description?.platform ?? undefined,
        awardingAuthority: tenderData.description?.awardingAuthority ?? undefined,
        details: tenderData.description?.details ?? undefined,
        permanent: tenderData.description?.permanent ?? false,
        mobile: tenderData.description?.mobile ?? false,
        alarm: tenderData.description?.alarm ?? false,
      },
      deadline: {
        publication: tenderData.deadline?.publication ? new Date(tenderData.deadline.publication) : undefined,
        questions: tenderData.deadline?.questions ? new Date(tenderData.deadline.questions) : undefined,
        outsideInformation: tenderData.deadline?.outsideInformation ? new Date(tenderData.deadline.outsideInformation) : undefined,
        siteVisit: tenderData.deadline?.siteVisit ? new Date(tenderData.deadline.siteVisit) : undefined,
        submission: tenderData.deadline?.submission ? new Date(tenderData.deadline.submission) : undefined,
      },
      estimate: {
        revenue: tenderData.estimate?.revenue ?? undefined,
        hoursPerMonth: tenderData.estimate?.hoursPerMonth ?? undefined,
        margin: convertBpToDecimalOrUndefined(tenderData.estimate?.margin ?? undefined),
        effort: tenderData.estimate?.effort ?? undefined,
        description: tenderData.estimate?.description ?? undefined,
      },
      contract: {
        escalationClause: convertBpToDecimalOrUndefined(tenderData.contract?.escalationClause ?? undefined),
        priceCriteria: convertBpToDecimalOrUndefined(tenderData.contract?.priceCriteria ?? undefined),
        qualityCriteria: convertBpToDecimalOrUndefined(tenderData.contract?.qualityCriteria ?? undefined),
        referenceCriteria: convertBpToDecimalOrUndefined(tenderData.contract?.referenceCriteria ?? undefined),
        paymentTerm: tenderData.contract?.paymentTerm ?? undefined,
      }
    },
    mode: "onChange"
  });

  const contractFormSchema = createContractDurationSchema(t);
  const contractForm = useForm<z.infer<typeof contractFormSchema>>({
    resolver: zodResolver(contractFormSchema),
    defaultValues: {
      contract: {
        startDate: tenderContractStartDate,
        endDate: tenderContractEndDate,
      }
    },
    mode: "onChange"
  });

  /**
   * Handles saving the form data
   */
  const handleSave = async () => {
    const isDetailFormValid = await detailsForm.trigger();
    const isContractFormValid = await contractForm.trigger();

    if (!isDetailFormValid || !isContractFormValid) {
      return;
    }

    const data = detailsForm.getValues();

    const { contract, estimate, ...rest } = data;
    const { escalationClause, ...restContract } = contract;
    const { margin, ...restEstimate } = estimate;

    const variables = {
      tenderId: Number(tenderData.id),
      input: {
        ...rest,
        estimate: {
          ...restEstimate,
          margin: margin ? convertDecimalToBp(margin) : undefined,
        },
        contract: {
          ...restContract,
          escalationClause: contract.escalationClause ? convertDecimalToBp(escalationClause as number) : undefined,
          priceCriteria: convertDecimalToBp(contract.priceCriteria as number),
          qualityCriteria: convertDecimalToBp(contract.qualityCriteria as number),
          referenceCriteria: convertDecimalToBp(contract.referenceCriteria as number),
        },
      }
    };

    // If tender type is private, remove fields that are not needed
    if (data.description.type === TenderType.Private) {
      delete variables.input.description.platform;
      delete variables.input.description.awardingAuthority;
      delete variables.input.deadline.questions;
      delete variables.input.deadline.siteVisit;
      delete variables.input.deadline.publication;
    }

    await updateTenderDetailsMutation({ variables });

    // if start date and end date on the contract form changed compared to its default values, update the contract duration
    const { startDate, endDate } = contractForm.getValues("contract");
    // Convert dates to timestamps for accurate comparison
    const startTimestamp = startDate?.getTime();
    const endTimestamp = endDate?.getTime();
    const originalStartTimestamp = tenderContractStartDate?.getTime();
    const originalEndTimestamp = tenderContractEndDate?.getTime();

    if (startTimestamp !== originalStartTimestamp || endTimestamp !== originalEndTimestamp) {
      if (hasServices) {
        toast(CustomToastComponent, {
          toastId: "starting-service-recreation",
          data: {
            title: t("toasts:toasts.startingservicesupdate"),
            type: "info",
          }
        });

        setContractDurationValues({
          startDate,
          endDate
        });

        setIsServiceRecalculatingDialogOpen(true);
      } else {
        await updateTenderContractDurationMutation({
          variables: {
            tenderId: Number(tenderData.id),
            startDate,
            endDate
          },
          onCompleted: () => {
            setIsEditing(false);
          }
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  // Expose handleSave to parent
  useImperativeHandle(ref, () => ({
    handleSave
  }));

  useEffect(() => {
    setIsSaving(isUpdatingTenderDetails || isUpdatingTenderContractDuration);
  }, [isUpdatingTenderDetails, isUpdatingTenderContractDuration]);

  return (
    <div>
      <Form {...detailsForm}>
        <div className="flex flex-col gap-4">
          <DescriptionFormGroup form={detailsForm} />
          <DeadlineFormGroup form={detailsForm} />
          <EstimateFormGroup form={detailsForm} />
          <ContractFormGroup form={detailsForm} />
        </div>
      </Form>
      <ContractDurationForm
        form={contractForm}
        hasServices={hasServices ?? false}
      />

      {
        isServiceRecalculatingDialogOpen && (
          <ServiceRecalculationDialog
            tenderId={Number(tenderData.id)}
            isOpen={isServiceRecalculatingDialogOpen}
            setIsOpen={setIsServiceRecalculatingDialogOpen}
            contractDurationValues={contractDurationValues}
            setIsEditing={setIsEditing}
          />
        )
      }
    </div>
  );
};

export default DetailsFormComponent;
